import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import noop from "lodash-es/noop";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { useBlocker, Location } from "react-router-dom";

import useAutofocusRef from "~frontend/util/useAutofocusRef";

interface Props {
  when?: boolean;
  shouldBlockNavigation?: (
    args:
      | {
          type: "unload";
        }
      | {
          type: "router";
          nextLocation: Location;
          currentLocation: Location;
        }
  ) => boolean | { title: string; body: string };
  title?: string | ReactNode;
  body?: string | ReactNode;
  cancelButtonText?: string | ReactNode;
  continueButtonText?: string | ReactNode;
  onCancel?: () => any;
}

export default function RouteLeavingGuard({
  when: isBlocked = true,
  shouldBlockNavigation = () => true,
  title = "You have unsaved changes",
  body = "Are you sure you want to discard your changes?",
  cancelButtonText = "Go Back",
  continueButtonText = "Discard & Continue",
  onCancel = noop,
}: Props): ReactElement {
  const [currentTitleBody, setCurrentTitleBody] = useState({
    title,
    body,
  });

  useEffect(() => {
    setCurrentTitleBody({ title, body });
  }, [title, body]);

  // This handles actual browser navigation (e.g. refreshing the page or clicking
  // an external link)
  useEffect(() => {
    if (isBlocked || shouldBlockNavigation) {
      window.onbeforeunload = () => {
        if (!isBlocked) {
          return;
        }

        if (shouldBlockNavigation({ type: "unload" })) {
          return true;
        }
      };
    } else {
      window.onbeforeunload = null;
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [isBlocked, shouldBlockNavigation]);

  // This handles react-router navigation
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (
      currentLocation.pathname === nextLocation.pathname &&
      currentLocation.search === nextLocation.search
    ) {
      // just a change in the hash; no need to block navigation
      return false;
    }

    if (nextLocation.state?.bypassRouteLeavingGuard) {
      return false;
    }

    if (!isBlocked) {
      return false;
    }

    const shouldBlock = shouldBlockNavigation({
      type: "router",
      nextLocation,
      currentLocation,
    });
    if (shouldBlock === true) {
      return true;
    } else if (shouldBlock === false) {
      return false;
    } else {
      setCurrentTitleBody(shouldBlock);
      return true;
    }
  });

  // If the state becomes unblocked while we're showing the dialog, reset
  useEffect(() => {
    if (blocker.state === "blocked" && !isBlocked) {
      blocker.reset();
    }
  }, [blocker, isBlocked]);

  const closeModal = () => {
    blocker.reset?.();
    onCancel();
  };

  const handleConfirmNavigationClick = () => {
    blocker.proceed?.();
  };

  const [confirmAutofocusRef] = useAutofocusRef();

  return (
    <>
      <Dialog
        open={blocker.state === "blocked"}
        onClose={closeModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        disableRestoreFocus
      >
        <DialogTitle id="alert-dialog-title">
          {currentTitleBody.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {currentTitleBody.body}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="secondary">
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirmNavigationClick}
            color="secondary"
            ref={confirmAutofocusRef}
          >
            {continueButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
